import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Avatar,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { ExistingUser } from '../../api/Auth/checkPhoneExists';

interface ExistingAccountModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    matchingUsers: ExistingUser[];
}

const ExistingAccountModal: React.FC<ExistingAccountModalProps> = ({
    open,
    onClose,
    onConfirm,
    matchingUsers,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, overflow: 'hidden' },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                    pt: 4,
                    pb: 3,
                    px: 3,
                    textAlign: 'center',
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                    Is this you?
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    We found an existing account with this phone number
                </Typography>
            </Box>

            <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
                {matchingUsers.map((user, index) => (
                    <Box
                        key={index}
                        sx={{
                            mb: index < matchingUsers.length - 1 ? 2 : 0,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2.5,
                            overflow: 'hidden',
                        }}
                    >
                        {/* User identity row */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                pb: 1.5,
                            }}
                        >
                            <Avatar
                                src={user.profile_photo || undefined}
                                sx={{
                                    width: 52,
                                    height: 52,
                                    bgcolor: user.role === 'coach' ? 'primary.main' : '#546e7a',
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                }}
                            >
                                {user.first_name?.[0]}
                                {user.last_name?.[0]}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 600, lineHeight: 1.3 }}
                                    >
                                        {user.first_name} {user.last_name}
                                    </Typography>
                                    <Chip
                                        label={user.role === 'coach' ? 'Coach' : 'Athlete'}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            borderRadius: '6px',
                                            bgcolor:
                                                user.role === 'coach' ? 'primary.main' : '#e0e0e0',
                                            color:
                                                user.role === 'coach' ? 'white' : 'text.secondary',
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Details section */}
                        <Box sx={{ px: 2, pb: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.75,
                                    bgcolor: '#f8f9fa',
                                    borderRadius: 1.5,
                                    p: 1.5,
                                }}
                            >
                                {user.phone && (
                                    <DetailRow
                                        icon={<PhoneOutlinedIcon sx={{ fontSize: 16 }} />}
                                        label={user.phone}
                                    />
                                )}
                                {user.email && (
                                    <DetailRow
                                        icon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />}
                                        label={user.email}
                                    />
                                )}
                                {user.created_at && (
                                    <DetailRow
                                        icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
                                        label={`Joined ${new Date(
                                            `${user.created_at}T00:00:00`,
                                        ).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                        })}`}
                                    />
                                )}
                                {user.organizations.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <GroupsOutlinedIcon
                                            sx={{
                                                fontSize: 16,
                                                color: 'text.secondary',
                                                mt: 0.5,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                            }}
                                        >
                                            {user.organizations.map((org, orgIndex) => (
                                                <Chip
                                                    key={orgIndex}
                                                    avatar={
                                                        org.logo ? (
                                                            <Avatar
                                                                src={org.logo}
                                                                sx={{ width: 18, height: 18 }}
                                                            />
                                                        ) : undefined
                                                    }
                                                    label={org.name}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        height: 24,
                                                        fontSize: '0.7rem',
                                                        borderRadius: '6px',
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                ))}
            </DialogContent>

            <DialogActions sx={{ flexDirection: 'column', px: 3, pb: 3, pt: 1, gap: 1 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onConfirm}
                    fullWidth
                    size="large"
                    sx={{
                        py: 1.5,
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                    }}
                >
                    Yes, this is me — Log In
                </Button>
                <Button
                    variant="text"
                    onClick={onClose}
                    fullWidth
                    size="small"
                    sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                    }}
                >
                    No, create a new account
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DetailRow: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: 'text.secondary', display: 'flex', flexShrink: 0 }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {label}
        </Typography>
    </Box>
);

export default ExistingAccountModal;
